<script setup lang="ts">
import { calculateMetricValue, DeltaType, MetricType, formatDeltaValue } from './utility';
import { NoResponsesVariationType } from '@Portal/models/NoResponsesVariationType';
import NoResponseComponent from '@Portal/components/NoResponseComponent.vue';
import { useFilterStore } from '@Portal/stores/filter';
import { SmartFilter } from '@Portal/models/smartFilters/SmartFilter';
import { DateRangeBounds } from '@Portal/legacy/types';
import dayjs from 'dayjs';
import { DATE_FORMAT } from '@Shared/constants';
import { roundAwayFromZero } from '@Shared/utils/util';

interface ImprovedLikeForLikeMainMetricTableResult {
  responseCountDelta: number;
  responseCountPercentage: number;
  mainMetricVersusTarget: number;
  previousResponseCount: number;
  currentResponseCount: number;
  previousMeasureValue: number;
  currentMeasureValue: number;
  measureValueDelta: number;
  mainMetricForFiscalYear: number;
}

interface ImprovedLikeForLikeDriverTableResult {
  previousMeasureValue: number;
  currentMeasureValue: number;
  measureValueDelta: number;
}

interface ImprovedLikeForLikeDimensionTableResult {
  previousResponseCount: number;
  currentResponseCount: number;
  previousMeasureValue: number;
  currentMeasureValue: number;
  measureValueDelta: number;
}

interface ImprovedLikeForLikeReportViewModel {
  count?: number;
  mainMetricTableResults: {
    title: string;
    metricType: MetricType;
    results: {
      Total: ImprovedLikeForLikeMainMetricTableResult;
      [tableRow: string]: ImprovedLikeForLikeMainMetricTableResult;
    };
  };

  driverTableResults: {
    title: string;
    metricType: MetricType;
    results: {
      [tableGroup: string]: {
        [tableRow: string]: ImprovedLikeForLikeDriverTableResult;
      };
    };
  };

  dimensionTableResults: {
    title: string;
    metricType: MetricType;
    results: {
      [tableRow: string]: ImprovedLikeForLikeDimensionTableResult;
    };
  };

  dateRangeBounds?: {
    previousPeriod?: DateRangeBounds;
    currentPeriod?: DateRangeBounds;
  };
}

const props = defineProps<ImprovedLikeForLikeReportViewModel>();

const sortedMainMetricTableRows = computed<[string, ImprovedLikeForLikeMainMetricTableResult][]>(() => [
  ['Total', props.mainMetricTableResults.results.Total] as [string, ImprovedLikeForLikeMainMetricTableResult],
  ...Object.entries(props.mainMetricTableResults.results).filter((row) => row[0] !== 'Total'),
]);

const filterStore = useFilterStore();

const formatDateRangeBounds = (period?: DateRangeBounds) => {
  if (!period) return '';
  let periodLabelText = '';
  const periodStartDate = period != null && period.startDateTime != null ? dayjs(period.startDateTime) : null;
  const periodEndDate = period != null && period.endDateTime != null ? dayjs(period.endDateTime) : null;

  if (periodStartDate !== null && periodEndDate !== null) {
    periodLabelText = periodStartDate.format(DATE_FORMAT);
    periodLabelText += ' - ' + periodEndDate.format(DATE_FORMAT);
  }

  return periodLabelText;
};

// Returns '{dateFilter} {suffix}' only if a date filter is set and its string value does not contain 'Year'
// and is not a date range in the format 'DD-MMM-YYYY - DD-MMM-YYYY'.
// If no date filter is set, or the filter is year-based (contains 'Year'), or is a date range, returns an empty string.
function getPeriodLabel(dateFilter: string | null, suffix: string): string {
  if (!dateFilter) return '';
  const dateRangeRegex = /^\d{2}-[A-Za-z]{3}-\d{4} - \d{2}-[A-Za-z]{3}-\d{4}$/;
  if (dateFilter.includes('Year') || dateRangeRegex.test(dateFilter)) return '';
  return `${dateFilter} ${suffix}`;
}

const currentPeriodLabel = computed(() =>
  getPeriodLabel(filterStore.dateFilter ? SmartFilter.toString(filterStore.dateFilter) : null, 'This Year')
);
const previousPeriodLabel = computed(() =>
  getPeriodLabel(filterStore.dateFilter ? SmartFilter.toString(filterStore.dateFilter) : null, 'Last Year')
);

const currentPeriodDatesLabel = computed<string>(() => formatDateRangeBounds(props.dateRangeBounds?.currentPeriod));
const previousPeriodDatesLabel = computed<string>(() => formatDateRangeBounds(props.dateRangeBounds?.previousPeriod));
</script>

<template>
  <div class="dashboard-likeforlike">
    <div v-if="!filterStore.dateFilter" class="dashboard-subtitle">
      Add a
      <span style="font-weight: 800; color: #c73362">date filter</span>
      to see how your CX is performing this period compared to the same period last year.
    </div>
    <div v-else class="dashboard-subtitle">
      Add
      <span style="font-weight: 800; color: #c73362">Region/Store/Channel filter</span>
      to see how your CX is performing this period compared to the same period last year.
    </div>
    <div v-if="props.count && props.count > 0 && !!filterStore.dateFilter">
      <!-- MainMetric table -->
      <div class="dashboard-grid-table dashboard-grid-table--main-metric">
        <div class="cell cell--header dashboard-likeforlike-tabletitle">
          {{ props.mainMetricTableResults.title }}
        </div>
        <div class="cell cell--header"></div>
        <div class="cell cell--header col-span-2" v-if="props!.dateRangeBounds!.previousPeriod">
          <span>
            <p>{{ previousPeriodLabel }}</p>
            <p class="font-italic">{{ previousPeriodDatesLabel }}</p>
          </span>
        </div>
        <div class="cell cell--header cell--group-start col-span-6" v-if="props!.dateRangeBounds!.currentPeriod">
          <span>
            <p>{{ currentPeriodLabel }}</p>
            <p class="font-italic">
              {{ currentPeriodDatesLabel }}
            </p>
          </span>
        </div>

        <div class="cell cell--header"></div>
        <div class="cell cell--header">NPS</div>
        <div class="cell cell--header">Response Count</div>
        <div class="cell cell--header cell--group-start">NPS</div>
        <div class="cell cell--header">Response Count</div>
        <div class="cell cell--header">NPS Delta</div>
        <div class="cell cell--header">Response Count Delta</div>
        <div class="cell cell--header">Response % Delta</div>
        <div class="cell cell--header">NPS vs Target</div>

        <!-- this is here because otherwise it looks weird with the outlines since the column label rows will be on top and it wont line up -->
        <div class="cell cell--header cell--group-start row-start-2 row-end-4 -col-start-2 -col-end-1">
          NPS Fiscal Year-to-Date
        </div>

        <div class="row-group" v-for="[rowHeading, rowData] in sortedMainMetricTableRows" :key="rowHeading">
          <div class="cell cell--data cell--data--row-label">
            {{ rowHeading + ' NPS' }}
          </div>
          <div class="cell cell--data">
            {{ calculateMetricValue(rowData.previousMeasureValue, props.mainMetricTableResults.metricType) }}
          </div>
          <div class="cell cell--data">
            {{ calculateMetricValue(rowData.previousResponseCount, MetricType.Count) }}
          </div>
          <div class="cell cell--data cell--group-start">
            {{ calculateMetricValue(rowData.currentMeasureValue, props.mainMetricTableResults.metricType) }}
          </div>
          <div class="cell cell--data">
            {{ calculateMetricValue(rowData.currentResponseCount, MetricType.Count) }}
          </div>
          <div class="cell cell--data">
            {{ formatDeltaValue(rowData.measureValueDelta, DeltaType.PointDelta) }}
          </div>
          <div class="cell cell--data">
            {{ formatDeltaValue(rowData.responseCountDelta, DeltaType.ResponseDelta) }}
          </div>
          <div class="cell cell--data">
            {{ formatDeltaValue(roundAwayFromZero(rowData.responseCountPercentage * 100), DeltaType.PercentageDelta) }}
          </div>
          <div class="cell cell--data">
            {{ formatDeltaValue(rowData.mainMetricVersusTarget, DeltaType.PointDelta) }}
          </div>
          <div class="cell cell--data cell--group-start">
            {{ calculateMetricValue(rowData.mainMetricForFiscalYear, props.mainMetricTableResults.metricType) }}
          </div>
        </div>
      </div>

      <!-- Drivers table -->
      <div class="dashboard-grid-table dashboard-grid-table--drivers">
        <div class="cell cell--header dashboard-likeforlike-tabletitle">
          {{ props.driverTableResults.title }}
        </div>
        <div class="cell cell--header"></div>
        <div class="cell cell--header" v-if="props!.dateRangeBounds!.previousPeriod">
          <span>
            <p>{{ previousPeriodLabel }}</p>
            <p class="font-italic">{{ previousPeriodDatesLabel }}</p>
          </span>
        </div>
        <div class="cell cell--header cell--group-start col-span-2" v-if="props!.dateRangeBounds!.currentPeriod">
          <span>
            <p>{{ currentPeriodLabel }}</p>
            <p class="font-italic">
              {{ currentPeriodDatesLabel }}
            </p>
          </span>
        </div>

        <div class="cell cell--header"></div>
        <div class="cell cell--header">Driver Score</div>
        <div class="cell cell--header cell--group-start cell--group-start">Driver Score</div>
        <div class="cell cell--header">Driver Delta</div>

        <template
          v-for="[groupHeading, groupRows] in Object.entries(props.driverTableResults.results)"
          :key="groupHeading"
        >
          <div class="row-group">
            <div class="cell cell--data cell--data--row-label">
              <span class="font-bold">{{ groupHeading }}</span>
            </div>
            <div class="cell cell--data cell--data--row-label col-span-3"></div>
          </div>
          <div class="row-group" v-for="[rowHeading, rowData] in Object.entries(groupRows)" :key="rowHeading">
            <div class="cell cell--data cell--data--row-label">
              {{ rowHeading }}
            </div>
            <div class="cell cell--data">
              {{ calculateMetricValue(rowData.previousMeasureValue, MetricType.TopBox) }}
            </div>
            <div class="cell cell--data cell--group-start">
              {{ calculateMetricValue(rowData.currentMeasureValue, MetricType.TopBox) }}
            </div>
            <div class="cell cell--data">
              {{ formatDeltaValue(roundAwayFromZero(rowData.measureValueDelta * 100), DeltaType.PercentageDelta) }}
            </div>
          </div>
        </template>
      </div>

      <!-- Dimensions Table -->
      <div class="dashboard-grid-table dashboard-grid-table--dimensions">
        <div class="cell cell--header dashboard-likeforlike-tabletitle">
          {{ props.dimensionTableResults.title }}
        </div>
        <div class="cell cell--header"></div>
        <div class="cell cell--header col-span-2" v-if="props!.dateRangeBounds!.previousPeriod">
          <span>
            <p>{{ previousPeriodLabel }}</p>
            <p class="font-italic">{{ previousPeriodDatesLabel }}</p>
          </span>
        </div>
        <div class="cell cell--header cell--group-start col-span-3" v-if="props!.dateRangeBounds!.currentPeriod">
          <span>
            <p>{{ currentPeriodLabel }}</p>
            <p class="font-italic">
              {{ currentPeriodDatesLabel }}
            </p>
          </span>
        </div>

        <div class="cell cell--header"></div>
        <div class="cell cell--header">NPS</div>
        <div class="cell cell--header">Response Count</div>
        <div class="cell cell--header cell--group-start">NPS</div>
        <div class="cell cell--header">Response Count</div>
        <div class="cell cell--header">NPS Delta</div>

        <div
          class="row-group"
          v-for="[rowHeading, rowData] in Object.entries(props.dimensionTableResults.results)"
          :key="rowHeading"
        >
          <div class="cell cell--data cell--data--row-label">
            {{ rowHeading }}
          </div>
          <div class="cell cell--data">
            {{ calculateMetricValue(rowData.previousMeasureValue, props.mainMetricTableResults.metricType) }}
          </div>
          <div class="cell cell--data">
            {{ calculateMetricValue(rowData.previousResponseCount, MetricType.Count) }}
          </div>
          <div class="cell cell--data cell--group-start">
            {{ calculateMetricValue(rowData.currentMeasureValue, props.mainMetricTableResults.metricType) }}
          </div>
          <div class="cell cell--data">
            {{ calculateMetricValue(rowData.currentResponseCount, MetricType.Count) }}
          </div>
          <div class="cell cell--data">
            {{ formatDeltaValue(rowData.measureValueDelta, DeltaType.PointDelta) }}
          </div>
        </div>
      </div>
    </div>
    <NoResponseComponent
      v-else-if="props.count === 0"
      :variation="NoResponsesVariationType.Default"
    ></NoResponseComponent>
  </div>
</template>

<style scoped lang="scss">
.dashboard-likeforlike {
  background-color: white;
  padding: 2rem;

  .dashboard-subtitle {
    margin-bottom: 1rem;
  }

  .dashboard-grid-table {
    display: grid;
    width: 100%;
    margin-bottom: 1rem;
    gap: 0.5px;

    &--main-metric {
      grid-template-columns: auto 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
    }

    &--drivers {
      grid-template-columns: auto 1fr 1fr 1fr;
    }

    &--dimensions {
      grid-template-columns: auto 1fr 1fr 1fr 1fr 1fr;
    }

    // all cells share this styling
    .cell {
      display: flex;
      text-align: center;
      align-items: center;
      justify-content: center;
      position: relative;

      outline: 0.5px solid #bdbdbd;
      color: #333333;
      padding-top: 8px;
      padding-bottom: 8px;
      padding-left: 16px;
      padding-right: 16px;
      font-family: 'Open Sans';
      font-weight: 600;
      font-size: 14px;
      line-height: 16px;

      &--header {
        background-color: #f2f2f2;
      }

      &--data {
        background-color: white;

        &--row-label {
          display: block;
          text-align: left;
          background: #f2f2f2;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      &--group-start {
        overflow: visible;
        &::after {
          content: '';
          position: absolute;
          top: -0.5px;
          left: -1px;
          width: 1px;
          height: calc(100% + 1px);
          background-color: #2c3647;
        }
      }
    }

    .row-group {
      display: contents;

      &:hover .cell--data {
        background-color: #cfd6e2;
      }
    }

    .dashboard-likeforlike-tabletitle {
      grid-column: 1 / -1;
      background-color: #2c3647;
      color: #ffffff;
      padding: 8px;
      padding-left: 16px;
      text-transform: uppercase;
      font-family: 'Open Sans';
      font-weight: 700;
      font-size: 14px;
      line-height: 19px;
    }
  }
}
</style>
